import {useEffect, useRef} from 'react';
import {useUiState} from '../Components/Context/UserInterface/UserInterfaceContext';
import {UserInterfaceActions} from '../Components/Context/UserInterface/UserInterfaceActionTypes';

interface UseKeyboardTipOnFocusProps {
  elementId?: string;
  elementRef?: React.RefObject<HTMLElement>;
  tipName: string;
}

export default function useKeyboardTipOnFocus({
  elementId,
  elementRef,
  tipName,
}: UseKeyboardTipOnFocusProps) {
  const {state: uiState, dispatch} = useUiState();
  const keyboardHelpState = useRef(uiState.tips.keyboardHelp);

  useEffect(() => {
    // Update the keyboard help state whenever it changes
    keyboardHelpState.current = uiState.tips.keyboardHelp;
  }, [uiState.tips.keyboardHelp]);

  useEffect(() => {
    let element: HTMLElement | null = null;

    // Get the element from the ref or ID
    if (elementRef && elementRef.current) {
      element = elementRef.current;
    } else if (elementId) {
      element = document.getElementById(elementId);
    }

    if (!element) return;

    const handleFocusIn = (e: FocusEvent) => {
      if (!element) return;

      const toolTipLocalStorage = window.localStorage.getItem('keyboardTip');
      const tooltip = toolTipLocalStorage ? JSON.parse(toolTipLocalStorage) : {};
      const canAutoShowKeyboardTip = !tooltip.hasOwnProperty(tipName);

      // Show the tip when focus enters the element
      if (!element.contains(e.relatedTarget as Node)) {
        if (keyboardHelpState.current.currentTip !== tipName) {
          dispatch(
            UserInterfaceActions.setKeyboardHelp({
              open: keyboardHelpState.current.open || canAutoShowKeyboardTip,
              currentTip: tipName,
            })
          );
        }
      }
    };

    const handleFocusOut = (e: FocusEvent) => {
      if (!element) return;

      // Remove the current tip when focus leaves the element (except to the body, as this is probably temporary while an action is being performed)
      if (
        e.relatedTarget &&
        !element.contains(e.relatedTarget as Node) &&
        (e.relatedTarget as HTMLElement).tagName !== 'BODY'
      ) {
        dispatch(
          UserInterfaceActions.setKeyboardHelp({
            open: keyboardHelpState.current.open,
            currentTip: null,
          })
        );
      }
    };

    element.addEventListener('focusin', handleFocusIn as EventListener);
    element.addEventListener('focusout', handleFocusOut as EventListener);

    return () => {
      element?.removeEventListener('focusin', handleFocusIn as EventListener);
      element?.removeEventListener('focusout', handleFocusOut as EventListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elementId, elementRef, tipName]);
}
